import { defineComponent, ref, nextTick } from 'vue';
import { ElMessage } from 'element-plus';
import {
  retrieveConfigInfo,
  createConfigInfo,
  updateConfigInfo,
  configGetServiceNames,
} from '@/api/message-center';
import type {
  IRetrieveConfigInfoRes,
  IConfigGetServiceNamesRes,
} from '@/api/message-center/type.d';
import { cloneDeep } from 'lodash-es';
import {
  CONFIG_IS_ENABLED_LIST,
  CONFIG_TYPE_LIST,
  HANDLE_TYPE_LIST,
  IS_LOCK_LIST,
  EXCHANGE_TYPE_ENUM_LIST,
} from '@/constant/message-center';
import { UserItem } from '@/api/auth-manage/user/type.d';
import { fuzzyQueryUser } from '@/api/auth-manage/user/index';
import { useFuzzy } from '@/hooks/use-fuzzy';

export default defineComponent({
  emits: ['update:visible', 'success'],
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    configId: {
      type: String,
      default: '',
    },
  },
  setup(props, { emit }) {
    const handleCloseDialog = () => {
      emit('update:visible', false);
    };

    const form = ref<IRetrieveConfigInfoRes>({
      configCode: '',
      exchange: '',
      exchangeType: '',
      queue: '',
      routing: '',
      sender: '',
      configId: '',
      executeService: '',
      configName: '',
      configType: '',
      handleType: '',
      hasLock: '',
      maxTryCount: 0,
      remark: '',
      isEnable: '1',
      consumerBoundUserId: '',
      consumerBoundUser: '',
      producerBoundUserId: '',
      producerBoundUser: '',
    });
    const refForm = ref();
    const isUpdate = ref(false);
    const handleOpenDialog = async () => {
      try {
        await nextTick();
        refForm.value.resetFields();
        const { configId } = props;
        isUpdate.value = !!configId;
        if (isUpdate.value) {
          const { data } = await retrieveConfigInfo(configId);
          form.value = data;
          form.value.maxTryCount = Number(form.value.maxTryCount) || 0;
        }
      } catch (e) {
        console.log(e);
      }
    };

    const handleSave = async () => {
      try {
        await nextTick();
        await refForm.value.validate();
        const params = cloneDeep(form.value);
        console.log(params);
        if (isUpdate.value) {
          await updateConfigInfo(params);
        } else {
          await createConfigInfo(params);
        }
        ElMessage.success('保存成功！');
        handleCloseDialog();
        emit('success');
      } catch (err) {
        console.log(err);
      }
    };

    const {
      fuzzyLoading: serviceLoading,
      fuzzyResponse: serviceResponse,
      fuzzyRemoteMethod: serviceRemoteMethod,
    } = useFuzzy<any, IConfigGetServiceNamesRes>({
      params: { keyword: '' },
      keywordKey: 'keyword',
      API: configGetServiceNames,
      beforeRequest(param, query) {
        return query;
      },
      beforeResponse(res) {
        return res.data?.map((serviceName: IConfigGetServiceNamesRes) => {
          return serviceName;
        });
      },
    });

    const {
      fuzzyLoading,
      fuzzyResponse,
      fuzzyRemoteMethod,
    } = useFuzzy<any, UserItem[]>({
      params: { keyword: '' },
      keywordKey: 'keyword',
      API: fuzzyQueryUser,
      beforeRequest(param, query) {
        return query;
      },
      beforeResponse(res) {
        return res.data?.map((user: UserItem) => {
          const { name: label, userId: value } = user;
          return { ...user, label, value };
        });
      },
    });

    const handleChangeUser = (key: string, userInfo: UserItem) => {
      const name = key as keyof IRetrieveConfigInfoRes;
      const userId = `${key}Id` as keyof IRetrieveConfigInfoRes;
      form.value[name] = userInfo.name as any;
      form.value[userId] = userInfo.userId as any;
    };

    return {
      handleCloseDialog,
      handleOpenDialog,
      form,
      refForm,
      isUpdate,
      handleSave,
      rules: {
        configCode: [
          {
            required: true,
            message: '配置编码不能为空',
          },
        ],
        exchange: [
          {
            required: true,
            message: '交换机不能为空',
          },
        ],
        exchangeType: [
          {
            required: true,
            message: '交换机类型不能为空',
          },
        ],
        configName: [
          {
            required: true,
            message: '配置名称不能为空',
          },
        ],
        consumerBoundUser: [
          {
            required: true,
            message: '消费者名称不能为空',
          },
        ],
        producerBoundUser: [
          {
            required: true,
            message: '生产者名称不能为空',
          },
        ],
      },
      CONFIG_IS_ENABLED_LIST,
      fuzzyLoading,
      fuzzyResponse,
      fuzzyRemoteMethod,
      handleChangeUser,
      serviceLoading,
      serviceResponse,
      serviceRemoteMethod,
    };
  },
  render() {
    return (
      <el-dialog
        title={this.isUpdate ? '编辑' : '新增'}
        modelValue={this.visible}
        onClose={this.handleCloseDialog}
        onOpen={this.handleOpenDialog}
        center
        width="800px"
        close-on-press-escape={false}
        close-on-click-modal={false}
        custom-class="el-dialog-inner-scroll"
        v-slots={{
          footer: () => (
            <div class="align-center">
              <el-button size="default" onClick={this.handleCloseDialog}>
                取 消
              </el-button>
              <el-button type="primary" size="default" onClick={this.handleSave}>
                保存
              </el-button>
            </div>
          ),
        }}
      >
        <el-form
          inline
          model={this.form}
          label-width="160px"
          size="default"
          rules={this.rules}
          ref="refForm"
        >
          <el-form-item label="配置编码：" prop="configCode">
            <el-input v-model={this.form.configCode} disabled={this.isUpdate} />
          </el-form-item>
          <el-form-item label="交换机：" prop="exchange">
            <el-input v-model={this.form.exchange} />
          </el-form-item>
          <el-form-item label="交换机类型：" prop="exchangeType">
          <el-select
              v-model={this.form.exchangeType}
              style="width: 200px;"
            >
              {EXCHANGE_TYPE_ENUM_LIST.map(item => (
                <el-option value={item.value} label={item.label} key={item.value} />
              ))}
            </el-select>
          </el-form-item>
          <el-form-item label="队列：" prop="queue">
            <el-input v-model={this.form.queue} />
          </el-form-item>
          <el-form-item label="路由：" prop="routing">
            <el-input v-model={this.form.routing} />
          </el-form-item>
          <el-form-item label="发送服务：" prop="sender">
            <el-select
                v-model={this.form.sender}
                placeholder="请输入"
                clearable
                remote
                filterable
                loading={this.serviceLoading}
                remote-method={this.serviceRemoteMethod}
                style="width: 200px;"
              >
                {this.serviceResponse.map(item => (
                  <el-option value={item} label={item}/>
                ))}
              </el-select>
          </el-form-item>
          <el-form-item label="执行服务：" prop="executeService">
            <el-select
                v-model={this.form.executeService}
                placeholder="请输入"
                clearable
                remote
                filterable
                loading={this.serviceLoading}
                remote-method={this.serviceRemoteMethod}
                style="width: 200px;"
              >
                {this.serviceResponse.map(item => (
                  <el-option value={item} label={item}/>
                ))}
              </el-select>
          </el-form-item>
          <el-form-item label="配置名称：" prop="configName">
            <el-input v-model={this.form.configName} />
          </el-form-item>
          <el-form-item label="配置类型：" prop="configType">
            <el-select
              v-model={this.form.configType}
              style="width: 200px;"
            >
              {CONFIG_TYPE_LIST.map(item => (
                <el-option value={item.value} label={item.label} key={item.value} />
              ))}
            </el-select>
          </el-form-item>
          <el-form-item label="处理类型：" prop="handleType">
            <el-select
              v-model={this.form.handleType}
              style="width: 200px;"
            >
              {HANDLE_TYPE_LIST.map(item => (
                <el-option value={item.value} label={item.label} key={item.value} />
              ))}
            </el-select>
          </el-form-item>
          <el-form-item label="是否需要加锁：" prop="hasLock">
            <el-select
              v-model={this.form.hasLock}
              style="width: 200px;"
            >
              {IS_LOCK_LIST.map(item => (
                <el-option value={item.value} label={item.label} key={item.value} />
              ))}
            </el-select>
          </el-form-item>
          <el-form-item label="最大重试次数：" prop="maxTryCount">
            <el-input-number v-model={this.form.maxTryCount} min={0} style="width: 200px;" />
          </el-form-item>
          <el-form-item label="配置备注信息：" prop="remark">
            <el-input v-model={this.form.remark} />
          </el-form-item>
          <el-form-item label="是否启用：" prop="isEnable">
            <el-select
              v-model={this.form.isEnable}
              style="width: 200px;"
            >
              {CONFIG_IS_ENABLED_LIST.map(item => (
                <el-option value={item.value} label={item.label} key={item.value} />
              ))}
            </el-select>
          </el-form-item>
          <el-form-item label="消费者负责人名称：" prop="consumerBoundUser">
            <el-select
              v-model={this.form.consumerBoundUser}
              placeholder="请输入"
              clearable
              remote
              filterable
              loading={this.fuzzyLoading}
              remote-method={this.fuzzyRemoteMethod}
              style="width: 200px;"
              onChange={(userInfo: UserItem) => this.handleChangeUser('consumerBoundUser', userInfo)}
            >
              {this.fuzzyResponse.map(item => (
                <el-option value={item} label={`${item.label}${item.bbCode}`} key={item.value} />
              ))}
            </el-select>
          </el-form-item>
          <el-form-item label="生产者负责人名称：" prop="producerBoundUser">
            <el-select
              v-model={this.form.producerBoundUser}
              placeholder="请输入"
              clearable
              remote
              filterable
              loading={this.fuzzyLoading}
              remote-method={this.fuzzyRemoteMethod}
              style="width: 200px;"
              onChange={(userInfo: UserItem) => this.handleChangeUser('producerBoundUser', userInfo)}
            >
              {this.fuzzyResponse.map(item => (
                <el-option value={item} label={`${item.label}${item.bbCode}`} key={item.value} />
              ))}
            </el-select>
          </el-form-item>
        </el-form>
      </el-dialog>
    );
  },
});
